import { useState } from 'react';
import useBreakpoint from '../utils/useBreakpoint';
import { Icone } from './ui';
import {
  ACCENT, OMBRE_CARTE, RAYON_CARTE, REMPLISSAGE_2, SEPARATEUR, SURFACE, TEXTE, TEXTE_DESACTIVE, TEXTE_MUET,
} from '../theme';

/**
 * Carte commande (visuel plein bord, infos en deux colonnes), commune aux niveaux "planning"
 * (confection) et "commande" (délavage, finition) : même anatomie pour éviter que les trois
 * écrans divergent visuellement au fil des retouches.
 *
 * `badge` accueille l'étiquette d'étape optionnelle (ex. "Avant lavage" côté confection).
 * `nbLivraisons` n'est affiché que si fourni : seul le planning confection connaît ce compte à
 * l'affichage de la liste (`livraisonsDepuisSousTraitant`) — délavage/finition ne le renvoient
 * qu'après ouverture de la commande, pas question de l'inventer ici.
 */
export default function CarteCommande({ info, identifiantSecours, badge, ctaLabel, nbLivraisons, onClick }) {
  const { compact } = useBreakpoint();
  return (
    <button
      type="button"
      className="aqs-carte"
      onClick={onClick}
      style={{
        textAlign: 'left', padding: 0, minWidth: 0, background: SURFACE, border: 'none', color: TEXTE,
        borderRadius: RAYON_CARTE, cursor: 'pointer', display: 'flex', alignItems: 'stretch', overflow: 'hidden',
        boxShadow: OMBRE_CARTE,
      }}
    >
      <VisuelPleinBord imageUrl={info.imageUrl} largeur={compact ? 108 : 148} />

      <div style={{ flex: 1, minWidth: 0, padding: compact ? '16px 18px' : '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, width: '100%', minWidth: 0, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: TEXTE_MUET }}>Commande</div>
            <div className="aqs-titre" style={{ marginTop: 2, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              {info.numero || identifiantSecours}
            </div>
          </div>
          {badge}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 18px', width: '100%' }}>
          <ChampCommande label="Modèle" valeur={info.modele} />
          <ChampCommande label="Client" valeur={info.client} />
          <ChampCommande label="Marque" valeur={info.marque} />
          <ChampCommande label="Quantité" valeur={info.qte} />
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', width: '100%', paddingTop: 14, borderTop: `0.5px solid ${SEPARATEUR}` }}>
          <span style={{ fontSize: 14, color: TEXTE_MUET }}>
            {nbLivraisons != null ? `${nbLivraisons} livraison${nbLivraisons > 1 ? 's' : ''}` : ' '}
          </span>
          <span style={{ fontSize: 15, fontWeight: 600, color: ACCENT, display: 'inline-flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
            {ctaLabel} <span className="aqs-carte-fleche" style={{ display: 'inline-flex' }}><Icone.ChevronDroite taille={18} /></span>
          </span>
        </div>
      </div>
    </button>
  );
}

function ChampCommande({ label, valeur }) {
  return (
    <div className="aqs-para" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: TEXTE_MUET }}>{label}</div>
      <div style={{ marginTop: 1, fontSize: 15, fontWeight: 550, color: TEXTE, lineHeight: 1.3 }}>{valeur || '—'}</div>
    </div>
  );
}

/** Vignette pleine hauteur, collée au bord gauche de la carte — distincte de `CommandeImage`
 * (vignette carrée en médaillon). En compact, elle se resserre pour laisser respirer les quatre
 * champs de la colonne de droite. */
function VisuelPleinBord({ imageUrl, largeur }) {
  const [enErreur, setEnErreur] = useState(false);
  const style = {
    width: largeur, flex: 'none', alignSelf: 'stretch', background: REMPLISSAGE_2,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  };

  if (!imageUrl || enErreur) {
    return (
      <div style={style}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 12, color: TEXTE_DESACTIVE }}>
          <Icone.Image taille={32} />
          <span style={{ fontSize: 12, color: TEXTE_MUET, textAlign: 'center' }}>Visuel du modèle</span>
        </div>
      </div>
    );
  }

  return (
    <div style={style}>
      <img
        src={imageUrl}
        alt="Modèle"
        onError={() => setEnErreur(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
}
